import { useState, useCallback, useMemo, useEffect, useEffectEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PageHeader } from '@/components/layout'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Plus, CheckSquare, Repeat, Square, Users, UserPlus } from 'lucide-react'
import {
  TareasKanban,
  TareasLista,
  TareaFormDialog,
  InscripcionFormDialog,
  TareaDetalleDialog,
  FiltrosTareas,
  TareasBulkActionsBar,
  type TareaFormValues,
} from '@/components/tareas'
import type { FiltroScope } from '@/core/components/tareas/FiltrosTareas'
import { useIsMobile } from '@/hooks/useIsMobile'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import {
  useTareas,
  useCrearTarea,
  useActualizarTarea,
  useEliminarTarea,
  useCambiarEstadoTarea,
} from '@/hooks/useTareas'
import { useCrearTareaRecurrente } from '@/core/hooks/useTareasRecurrentes'
import { useCrearTareaParaGrupo } from '@/core/hooks/useTareasGrupo'
import { hoyEnArgentina } from '@/core/services/tareasRecurrentesService'
import type { GrupoDeTablero } from '@/core/services/tareasGrupoService'
import { formularioARecurrente } from '@/components/tareas/forms/formularioARecurrente'
import { RecurrentesDialog } from '@/core/components/tareas/RecurrentesDialog'
import { TareaGrupoDetalleDialog } from '@/core/components/tareas/TareaDetalleDialog'
import { useConfirmarCompletar } from '@/core/components/tareas/ConfirmarCompletarDialog'
import { useSubirDocumento, useVincularDocumento } from '@/core/hooks/useDocumentos'
import type { TareaCompleta, EstadoTarea } from '@/types/database'
import { usePermissions } from '@/hooks/usePermissions'
import { toast } from 'sonner'
import { ErrorParaElUsuario, handleMutationError, puedeTocarTarea } from '@/lib/permisos-guard'

/** ORR-381: el texto del toast cuando la acción masiva saltea tareas que no son tuyas. */
const formatearSalteadas = (cantidad: number): string => {
  if (cantidad === 0) return ''
  if (cantidad === 1) return ' Se salteó 1 tarea que no es tuya.'
  return ` Se saltearon ${cantidad} tareas que no son tuyas.`
}

export function Tareas() {
  const { esAdmin, emailUsuario, puede } = usePermissions()
  const isMobile = useIsMobile()
  // ORR-200 B3 / ORR-381: la regla vive en `puedeTocarTarea`, compartida con los hooks y con las
  // otras pantallas. Antes estaba copiada acá, en la ficha del cliente y adentro de useTareas.
  const puedeEditarTarea = (tarea: TareaCompleta) => puedeTocarTarea(tarea, puede, emailUsuario)
  // Estados de UI
  const [vistaKanban, setVistaKanban] = useState(true)
  // ORR-398: arranca en "Míos", igual que el interruptor del calendario, y la preferencia se
  // recuerda por navegador. La clave lleva el usuario porque en un navegador compartido el segundo
  // en entrar heredaría la vista del primero; mientras la sesión no resolvió es la anónima, y el
  // hook re-lee cuando aparece el email.
  const [filtroScopeGuardado, setFiltroScope] = useLocalStorage<FiltroScope>(
    `tareas-filtro-scope:${emailUsuario ?? 'anon'}`,
    'mios',
  )
  // El interruptor lo ven owner y admin, igual que en el calendario. El resto queda fijo en
  // "Míos", aunque tenga otro alcance guardado en el navegador.
  const filtroScope: FiltroScope = esAdmin ? filtroScopeGuardado : 'mios'
  const [formDialogOpen, setFormDialogOpen] = useState(false)
  const [detalleDialogOpen, setDetalleDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [tareaSeleccionada, setTareaSeleccionada] = useState<TareaCompleta | null>(null)

  // `?nuevo=true` abre el alta de tarea si hay permiso de crear, y se limpia de la URL.
  const [searchParams, setSearchParams] = useSearchParams()
  const abrirAltaDesdeUrl = useEffectEvent(() => {
    if (!puede.crearTarea) return
    setTareaSeleccionada(null)
    setFormDialogOpen(true)
  })
  useEffect(() => {
    if (searchParams.get('nuevo') !== 'true') return
    abrirAltaDesdeUrl()
    setSearchParams({}, { replace: true })
  }, [searchParams, setSearchParams])

  // Estados de filtros
  const [busqueda, setBusqueda] = useState('')
  const [filtroAsignado, setFiltroAsignado] = useState('')
  const [filtroCliente, setFiltroCliente] = useState('')
  const [mostrarCompletadas, setMostrarCompletadas] = useState(false)
  const [completadasDesde, setCompletadasDesde] = useState<string | null>(null)
  const [completadasHasta, setCompletadasHasta] = useState<string | null>(null)

  // Estado de selección múltiple
  const [selectionMode, setSelectionMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [isDeleting, setIsDeleting] = useState(false)
  const [isChangingEstado, setIsChangingEstado] = useState(false)

  // El "Asignado a" solo tiene sentido en "Todo el estudio": en las otras dos posiciones el
  // alcance ya lo fija el interruptor. Ocultarlo y aplicarlo salen de la MISMA condición a
  // propósito — si divergen, un "Asignado a" elegido antes de mover el interruptor se sigue
  // aplicando con AND desde un control que ya no está a la vista, y la pantalla queda vacía sin
  // nada que lo explique.
  const filtroAsignadoVisible = filtroScope === 'estudio'

  const filtroAsignadoEfectivo = filtroAsignadoVisible ? filtroAsignado : ''

  const filtroAsignadoPorEfectivo = filtroScope === 'asignadas_por_mi'
    ? (emailUsuario ?? '')
    : undefined

  // "Míos" es la tarea asignada a mí. No se pisa con "Asigné", que mira `asignado_por`.
  const filtroPropiasEfectivo = filtroScope === 'mios'
    ? (emailUsuario ?? undefined)
    : undefined

  // Handler para toggle de completadas: setea default últimos 7 días al activar
  const handleMostrarCompletadasChange = useCallback((value: boolean) => {
    setMostrarCompletadas(value)
    if (value) {
      const hoyISO = hoyEnArgentina()
      if (!completadasDesde) {
        const hace7dias = new Date(`${hoyISO}T00:00:00Z`)
        hace7dias.setUTCDate(hace7dias.getUTCDate() - 7)
        setCompletadasDesde(hace7dias.toISOString().slice(0, 10))
      }
      if (!completadasHasta) {
        setCompletadasHasta(hoyISO)
      }
    } else {
      setCompletadasDesde(null)
      setCompletadasHasta(null)
    }
  }, [completadasDesde, completadasHasta])

  // Query para obtener total de tareas (para seleccionar todas)
  const { data: tareasRaw = [] } = useTareas({
    asignado_a: filtroAsignadoEfectivo || undefined,
    asignado_por: filtroAsignadoPorEfectivo,
    propias_de: filtroPropiasEfectivo,
    cliente_id: filtroCliente || undefined,
    busqueda: busqueda || undefined,
    mostrarCompletadas,
    completadasDesde: mostrarCompletadas ? completadasDesde : undefined,
    completadasHasta: mostrarCompletadas ? completadasHasta : undefined,
  })

  const todasLasTareas = useMemo(() => tareasRaw, [tareasRaw])

  // El vacío que hay que explicar es el del interruptor, no el de una búsqueda: con un filtro de
  // texto o de cliente puesto, cero resultados ya se explica solo.
  const sinTareasPropias =
    esAdmin &&
    filtroScope === 'mios' &&
    todasLasTareas.length === 0 &&
    !busqueda &&
    !filtroCliente &&
    !mostrarCompletadas

  // Mutations
  const crearTarea = useCrearTarea()
  const crearRecurrente = useCrearTareaRecurrente()
  const crearTareaGrupo = useCrearTareaParaGrupo()
  const [recurrentesOpen, setRecurrentesOpen] = useState(false)
  const [inscripcionOpen, setInscripcionOpen] = useState(false)
  // `grupo_clave` del grupo abierto en el detalle, o null.
  const [grupoAbierto, setGrupoAbierto] = useState<string | null>(null)
  const actualizarTarea = useActualizarTarea()
  const eliminarTarea = useEliminarTarea()
  const cambiarEstado = useCambiarEstadoTarea()
  const { confirmar, dialogo: confirmarCompletarDialogo } = useConfirmarCompletar()
  const subirDocumento = useSubirDocumento()
  const vincularDocumento = useVincularDocumento()

  // Handlers de selección múltiple
  const toggleSelectionMode = useCallback(() => {
    setSelectionMode((prev) => !prev)
    setSelectedIds(new Set())
  }, [])

  const handleSelectionChange = useCallback((id: string, selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (selected) {
        next.add(id)
      } else {
        next.delete(id)
      }
      return next
    })
  }, [])

  const handleSelectAll = useCallback(() => {
    setSelectedIds(new Set(todasLasTareas.map((t) => t.id)))
  }, [todasLasTareas])

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set())
    setSelectionMode(false)
  }, [])

  const handleDeleteSelected = useCallback(async () => {
    const idsToDelete = Array.from(selectedIds)
    if (idsToDelete.length === 0) return

    setIsDeleting(true)
    let successCount = 0
    let errorCount = 0
    // El texto de un error escrito para el usuario, como el de reintentar mientras corre el cron.
    let errorParaElUsuario: string | null = null

    for (const id of idsToDelete) {
      try {
        await eliminarTarea.mutateAsync(id)
        successCount++
      } catch (error) {
        console.error(`Error eliminando tarea ${id}:`, error)
        errorCount++
        if (error instanceof ErrorParaElUsuario) errorParaElUsuario = error.message
      }
    }

    setIsDeleting(false)
    setSelectedIds(new Set())
    setSelectionMode(false)

    if (errorCount === 0) {
      toast.success(
        successCount === 1
          ? 'Tarea eliminada'
          : `${successCount} tareas eliminadas`
      )
    } else {
      toast.error(
        `Se eliminaron ${successCount} tareas, pero ${errorCount} fallaron${errorParaElUsuario ? `. ${errorParaElUsuario}` : ''}`
      )
    }
  }, [selectedIds, eliminarTarea])

  const handleChangeEstadoSelected = useCallback(async (nuevoEstado: EstadoTarea) => {
    const idsToChange = Array.from(selectedIds)
    if (idsToChange.length === 0) return

    setIsChangingEstado(true)
    let successCount = 0
    let errorCount = 0
    // ORR-381: el lote se filtra ANTES de mutar. La acción masiva se ofrecía con `crearTarea` y no
    // miraba de quién era cada tarea, así que seleccionar diez ajenas devolvía diez errores
    // seguidos del guard del hook. Se cuentan aparte para poder decirlo en el toast.
    let sinPermisoCount = 0

    for (const id of idsToChange) {
      const tarea = todasLasTareas.find((t) => t.id === id)
      if (!tarea || tarea.estado === nuevoEstado) continue
      if (!puedeTocarTarea(tarea, puede, emailUsuario)) {
        sinPermisoCount++
        continue
      }

      try {
        await cambiarEstado.mutateAsync({
          id,
          estado: nuevoEstado,
          estadoAnterior: tarea.estado as EstadoTarea,
          enLote: true,
        })
        successCount++
      } catch (error) {
        console.error(`Error cambiando estado de tarea ${id}:`, error)
        errorCount++
      }
    }

    setIsChangingEstado(false)
    setSelectedIds(new Set())
    setSelectionMode(false)

    const salteadas = formatearSalteadas(sinPermisoCount)

    if (errorCount === 0 && successCount > 0) {
      toast.success(
        (successCount === 1
          ? 'Estado actualizado'
          : `${successCount} tareas actualizadas`) + salteadas
      )
    } else if (errorCount > 0) {
      toast.error(
        `Se actualizaron ${successCount} tareas, pero ${errorCount} fallaron.${salteadas}`
      )
    } else if (sinPermisoCount > 0) {
      // Ninguna se pudo tocar: sin este caso la selección se limpia y no pasa nada visible.
      toast.error(salteadas.trim())
    }
  }, [selectedIds, todasLasTareas, cambiarEstado, puede, emailUsuario])

  // Handlers
  const handleNuevaTarea = () => {
    setTareaSeleccionada(null)
    setFormDialogOpen(true)
  }

  const handleVerDetalle = (tarea: TareaCompleta) => {
    setTareaSeleccionada(tarea)
    setDetalleDialogOpen(true)
  }

  const handleVerGrupo = (grupo: GrupoDeTablero) => setGrupoAbierto(grupo.clave)

  // Desde el detalle del grupo se abre el de una de sus tareas, y el del grupo se cierra.
  const handleVerTareaDeGrupo = (tarea: TareaCompleta) => {
    setGrupoAbierto(null)
    handleVerDetalle(tarea)
  }

  const handleEditar = (tarea: TareaCompleta) => {
    setTareaSeleccionada(tarea)
    setDetalleDialogOpen(false)
    setFormDialogOpen(true)
  }

  const handleEliminar = (tarea: TareaCompleta) => {
    setTareaSeleccionada(tarea)
    setDeleteDialogOpen(true)
  }

  const handleSubmitForm = async (data: TareaFormValues, archivos?: File[]) => {
    try {
      if (tareaSeleccionada) {
        await actualizarTarea.mutateAsync({
          id: tareaSeleccionada.id,
          datos: data,
        })
        toast.success('Tarea actualizada correctamente')
      } else if (data.repetir) {
        // Repetición (ORR-304): se guarda la regla, no una tarea suelta. La
        // primera ocurrencia la crea el hook si su fecha ya llegó; el resto, el
        // cron diario.
        await crearRecurrente.mutateAsync({
          ...formularioARecurrente(data),
          workspace: 'contable',
          fecha_inicio: data.fecha_limite || hoyEnArgentina(),
        })
        if (archivos && archivos.length > 0) {
          toast.warning(
            'La repetición se guardó. Los archivos no se adjuntaron: se adjuntan sobre cada tarea generada.'
          )
        }
      } else if (data.grupo_tipo && data.grupo_valor) {
        // Grupo de clientes: una tarea por cliente activo de la etiqueta, la
        // condición IVA o el impuesto elegido. Sin repetición: la próxima vez hay
        // que volver a elegir el grupo.
        const { creadas } = await crearTareaGrupo.mutateAsync({
          titulo: data.titulo,
          descripcion: data.descripcion ?? null,
          asignado_a: data.asignado_a || null,
          prioridad: data.prioridad,
          fecha_limite: data.fecha_limite || null,
          workspace: 'contable',
          tipo: data.grupo_tipo,
          valor: data.grupo_valor,
        })
        if (creadas > 0 && archivos && archivos.length > 0) {
          toast.warning(
            'Las tareas se crearon. Los archivos no se adjuntaron: se adjuntan sobre cada tarea generada.'
          )
        }
      } else {
        const tarea = await crearTarea.mutateAsync(data)
        // Upload diferido (ORR-148): subir+vincular los adjuntos elegidos durante la
        // creación, ya con el id de la tarea. Best-effort: la tarea no se revierte.
        let fallos = 0
        for (const file of archivos ?? []) {
          try {
            const doc = await subirDocumento.mutateAsync({
              file,
              clienteId: data.cliente_id ?? '',
              createdBy: emailUsuario ?? '',
              metadatos: { nombre: file.name, categoria: 'otro' },
            })
            await vincularDocumento.mutateAsync({
              documentoId: doc.id,
              entidadTipo: 'tarea',
              entidadId: tarea.id,
              rol: 'adjunto',
            })
          } catch (e) {
            console.error('Error adjuntando archivo a tarea nueva:', e)
            fallos++
          }
        }
        if (fallos > 0) {
          toast.warning(`Tarea creada. ${fallos} archivo(s) no se pudieron adjuntar.`)
        } else {
          toast.success('Tarea creada correctamente')
        }
      }
      setFormDialogOpen(false)
      setTareaSeleccionada(null)
    } catch (error) {
      // onError in useCrearTarea/useActualizarTarea already handles the toast
      console.error(error)
    }
  }

  const handleConfirmarEliminar = async () => {
    if (!tareaSeleccionada) return

    try {
      await eliminarTarea.mutateAsync(tareaSeleccionada.id)
      toast.success('Tarea eliminada correctamente')
      setDeleteDialogOpen(false)
      setTareaSeleccionada(null)
    } catch (error) {
      handleMutationError(error, 'Error al eliminar la tarea')
    }
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Tareas"
        description="Gestiona las tareas del equipo con tablero Kanban"
      />

      <div className="flex-1 p-4 md:p-6 space-y-4 md:space-y-6">
        {/* Filtros y acciones */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-between items-start sm:items-center">
          <div className="flex-1 w-full">
            <FiltrosTareas
              busqueda={busqueda}
              onBusquedaChange={setBusqueda}
              asignadoA={filtroAsignado}
              onAsignadoAChange={setFiltroAsignado}
              clienteId={filtroCliente}
              onClienteIdChange={setFiltroCliente}
              vistaKanban={!isMobile && vistaKanban}
              onVistaChange={setVistaKanban}
              filtroScope={filtroScope}
              onFiltroScopeChange={esAdmin ? setFiltroScope : undefined}
              hideAsignadoFilter={!filtroAsignadoVisible}
              mostrarCompletadas={mostrarCompletadas}
              onMostrarCompletadasChange={handleMostrarCompletadasChange}
              completadasDesde={completadasDesde}
              onCompletadasDesdeChange={setCompletadasDesde}
              completadasHasta={completadasHasta}
              onCompletadasHastaChange={setCompletadasHasta}
            />
          </div>
          <div className="flex items-center gap-2">
            {!isMobile && todasLasTareas.length > 0 && (puede.eliminarTarea || puede.editarTarea) && (
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant={selectionMode ? 'secondary' : 'outline'}
                      size="icon"
                      onClick={toggleSelectionMode}
                      className="h-9 w-9"
                    >
                      {selectionMode ? (
                        <CheckSquare className="h-4 w-4" />
                      ) : (
                        <Square className="h-4 w-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {selectionMode ? 'Cancelar selección' : 'Seleccionar múltiples'}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {puede.verTareas && (
              <Button
                variant="outline"
                size={isMobile ? 'sm' : 'default'}
                onClick={() => setRecurrentesOpen(true)}
              >
                <Repeat className="h-4 w-4 mr-2" />
                Repetitivas
              </Button>
            )}
            {puede.crearTarea && (
              <Button
                variant="outline"
                size={isMobile ? 'sm' : 'default'}
                onClick={() => setInscripcionOpen(true)}
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Nueva inscripción
              </Button>
            )}
            {puede.crearTarea && (
              <Button onClick={handleNuevaTarea} size={isMobile ? 'sm' : 'default'}>
                <Plus className="h-4 w-4 mr-2" />
                Nueva Tarea
              </Button>
            )}
          </div>
        </div>

        {/* Barra de acciones bulk */}
        {confirmarCompletarDialogo}
        {selectionMode && selectedIds.size > 0 && (
          <TareasBulkActionsBar
            selectedCount={selectedIds.size}
            totalCount={todasLasTareas.length}
            onSelectAll={handleSelectAll}
            onClearSelection={handleClearSelection}
            onDeleteSelected={puede.eliminarTarea ? handleDeleteSelected : undefined}
            onChangeEstado={
              puede.crearTarea
                ? async (estado) => confirmar(estado, () => void handleChangeEstadoSelected(estado), selectedIds.size)
                : undefined
            }
            isDeleting={isDeleting}
            isChangingEstado={isChangingEstado}
          />
        )}

        {/* Con "Míos" por default y cero resultados el tablero nace vacío y parece roto. Pasó en
            la planilla del calendario (ORR-367) y la salida es la misma: decir por qué está vacío
            y ofrecer el otro lado del interruptor en el mismo lugar donde se lee el problema. */}
        {sinTareasPropias && (
          <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground flex items-center justify-between gap-4">
            <span>No tenés tareas asignadas.</span>
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setFiltroScope('estudio')}>
              <Users className="h-3.5 w-3.5 mr-1.5" />
              Ver todo el estudio
            </Button>
          </div>
        )}

        {/* Vista: mobile siempre lista, desktop respeta toggle */}
        {!isMobile && vistaKanban ? (
          <TareasKanban
            filtroAsignado={filtroAsignadoEfectivo || undefined}
            filtroAsignadoPor={filtroAsignadoPorEfectivo}
            filtroPropias={filtroPropiasEfectivo}
            filtroCliente={filtroCliente || undefined}
            filtroBusqueda={busqueda || undefined}
            mostrarCompletadas={mostrarCompletadas}
            completadasDesde={completadasDesde}
            completadasHasta={completadasHasta}
            onVerDetalle={handleVerDetalle}
            onEditar={handleEditar}
            onEliminar={handleEliminar}
            onVerGrupo={handleVerGrupo}
            canEdit={puedeEditarTarea}
            canDelete={puede.eliminarTarea}
          />
        ) : (
          <TareasLista
            filtroAsignado={filtroAsignadoEfectivo || undefined}
            filtroAsignadoPor={filtroAsignadoPorEfectivo}
            filtroPropias={filtroPropiasEfectivo}
            filtroCliente={filtroCliente || undefined}
            filtroBusqueda={busqueda || undefined}
            mostrarCompletadas={mostrarCompletadas}
            completadasDesde={completadasDesde}
            completadasHasta={completadasHasta}
            onVerDetalle={handleVerDetalle}
            onEditar={handleEditar}
            onEliminar={handleEliminar}
            // En mobile la lista no agrupa: cada tarea es su propia fila.
            onVerGrupo={isMobile ? undefined : handleVerGrupo}
            canEdit={puedeEditarTarea}
            canDelete={puede.eliminarTarea}
            canChangeEstado={puedeEditarTarea}
            selectionMode={selectionMode}
            selectedIds={selectedIds}
            onSelectionChange={handleSelectionChange}
          />
        )}
      </div>

      {/* Dialogs */}
      <RecurrentesDialog open={recurrentesOpen} onOpenChange={setRecurrentesOpen} />
      <InscripcionFormDialog open={inscripcionOpen} onOpenChange={setInscripcionOpen} />

      <TareaFormDialog
        open={formDialogOpen}
        onOpenChange={setFormDialogOpen}
        tarea={tareaSeleccionada}
        onSubmit={handleSubmitForm}
        isLoading={
          crearTarea.isPending ||
          crearTareaGrupo.isPending ||
          crearRecurrente.isPending ||
          actualizarTarea.isPending ||
          subirDocumento.isPending ||
          vincularDocumento.isPending
        }
        canAttach={puede.subirDocumento}
        canRepeat
      />

      <TareaDetalleDialog
        open={detalleDialogOpen}
        onOpenChange={setDetalleDialogOpen}
        tarea={tareaSeleccionada}
        onEditar={tareaSeleccionada && puedeEditarTarea(tareaSeleccionada) ? handleEditar : undefined}
        // ORR-347 / ORR-381: el mismo gate que la lista de esta pantalla. Ahora mira de quién es
        // la tarea, no solo el rol: un asistente no cambia el estado de una tarea ajena.
        canChangeEstado={!!tareaSeleccionada && puedeEditarTarea(tareaSeleccionada)}
      />

      <TareaGrupoDetalleDialog
        open={grupoAbierto !== null}
        onOpenChange={(open) => !open && setGrupoAbierto(null)}
        clave={grupoAbierto}
        onVerTarea={handleVerTareaDeGrupo}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar tarea</AlertDialogTitle>
            <AlertDialogDescription>
              Esta accion no se puede deshacer. Se eliminara permanentemente la tarea
              "{tareaSeleccionada?.titulo}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmarEliminar}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
